import React, { useState, useMemo } from "react";
import TestCaseEditor from "../TestCaseEditor";
import AddWebsiteTestCasesSection from "../AddWebsiteTestCasesSection";
import {
  useDeleteStagingTestCasesMutation,
  useDeleteProdTestCasesMutation,
} from "../../../../../app/api/console/scanTemplateSyncApiSlice";

const TestCasesTab = ({ template, environment, templateId, websiteMap }) => {
  const [deleteTarget, setDeleteTarget] = useState(null);

  const [deleteStagingTestCases, { isLoading: isDeletingStaging }] = useDeleteStagingTestCasesMutation();
  const [deleteProdTestCases, { isLoading: isDeletingProd }] = useDeleteProdTestCasesMutation();
  const isDeleting = environment === "staging" ? isDeletingStaging : isDeletingProd;

  const baseTestCases = template.base_test_cases;
  // Not memoised on purpose: `template.website_test_cases` is a stable prop reference and
  // the `[]` fallback only applies when the template has none; wrapping it in useMemo would
  // change its identity semantics for the `websiteTestCaseIds` memo below.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const websiteTestCases = template.website_test_cases || [];
  const allContainers = template.test_cases_containers || [];
  const hasAny = allContainers.length > 0;
  const isAgenticMode = template.scan_mode === "agentic_test" || template.scan_mode === "agentic_test_definition";

  const websiteTestCaseIds = useMemo(() => {
    return websiteTestCases.map((c) => c.website_id).filter(Boolean);
  }, [websiteTestCases]);

  const handleDeleteTestCases = async () => {
    if (!deleteTarget) return;
    const deleteFn = environment === "staging" ? deleteStagingTestCases : deleteProdTestCases;
    await deleteFn({ containerId: deleteTarget.container_id });
    setDeleteTarget(null);
  };

  if (!isAgenticMode) {
    return (
      <div className="py-12 text-center">
        <div className="w-12 h-12 rounded-full bg-gray-100 flex items-center justify-center mx-auto mb-3">
          <svg className="w-6 h-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M9.75 9.75l4.5 4.5m0-4.5l-4.5 4.5M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <h3 className="text-sm font-medium text-gray-900">Not applicable</h3>
        <p className="text-sm text-gray-500 mt-1">
          Test cases are only used for <span className="font-medium">Agentic Test</span> and <span className="font-medium">Discovery</span> modes.
          <br />This template uses <span className="font-medium">{template.scan_mode}</span>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Test Cases</h3>
          <p className="text-sm text-gray-500 mt-0.5">
            {hasAny ? (
              <>
                {allContainers.length} container{allContainers.length !== 1 ? "s" : ""}
                {" "}&mdash;{" "}
                {allContainers.reduce((sum, c) => sum + (c.current_test_cases?.length || 0), 0)} total test cases
              </>
            ) : (
              "No test cases defined yet"
            )}
          </p>
        </div>
      </div>

      {baseTestCases && (
        <TestCaseEditor
          container={baseTestCases}
          environment={environment}
          templateId={templateId}
          onDelete={() => setDeleteTarget({ container_id: baseTestCases.container_id, label: "Base Test Cases" })}
        />
      )}

      {websiteTestCases.length > 0 && (
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            Website-Specific Test Cases
          </h4>
          {websiteTestCases.map((container) => (
            <TestCaseEditor
              key={container.container_id}
              container={container}
              environment={environment}
              templateId={templateId}
              websiteName={websiteMap?.[container.website_id]}
              onDelete={() => setDeleteTarget({ container_id: container.container_id, label: websiteMap?.[container.website_id] || container.website_id })}
            />
          ))}
        </div>
      )}

      <AddWebsiteTestCasesSection
        templateId={templateId}
        environment={environment}
        hasBaseTestCases={!!baseTestCases}
        websiteTestCaseIds={websiteTestCaseIds}
      />

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl border border-border-subtle">
            <h3 className="text-body-lg font-semibold text-text-primary mb-2">Delete Test Cases</h3>
            <p className="text-body-sm text-text-secondary mb-5">
              Are you sure you want to delete <strong>{deleteTarget.label}</strong>? All versions will be permanently removed. This cannot be undone.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteTarget(null)}
                className="px-4 py-2 border border-border-default rounded-xl text-body-sm text-text-secondary hover:bg-surface-neutral transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteTestCases}
                disabled={isDeleting}
                className="px-4 py-2 bg-severity-high text-white rounded-xl text-body-sm font-semibold hover:opacity-90 disabled:opacity-40 transition-all"
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TestCasesTab;
